import {Injectable} from '@angular/core';
import {finalize, Observable} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import {environment} from '../../../../src/environments/environment';
import {SearchParams} from '../widgets/widgets.interface';
import {Dashboard} from './dashboard.model';
import {IParamSearchModel} from '../paramlist/searchparam.model';
import {components, operations} from '../_types/api-types';
import {map, shareReplay, tap} from 'rxjs/operators';
import {IconModel} from '../icon/icon.model';
import {of} from 'rxjs/internal/observable/of';
import {ServiceDashboardClass} from '@inspark/widget-common/full';

@Injectable()
export class DashboardApiService {

  private dashboards: Dashboard[] | null = null;
  private dashboards$: Observable<Dashboard[]> | null = null;
  private lastFetchTime: number | null = null;
  private readonly CACHE_TTL_MS = 5 * 1000; // 5 сек

  constructor(private http: HttpClient) {
  }

  getAll(): Observable<Dashboard[]> {
    const now = Date.now();

    if (this.dashboards && this.lastFetchTime && (now - this.lastFetchTime < this.CACHE_TTL_MS)) {
      return of(this.dashboards);
    }
    if (this.dashboards$) {
      return this.dashboards$;
    }
    this.dashboards$ = this.http.get<Dashboard[]>('/wg/dashboard').pipe(
      tap(dashboards => {
        this.lastFetchTime = Date.now();
        this.dashboards = dashboards;
        this.dashboards$ = null;
      }),
      shareReplay(1),
      finalize(() => {
        this.dashboards$ = null;
      })
    );

    return this.dashboards$;
  }

  byRubric(idRubric, isMobile): Observable<Dashboard[]> {
    return this.http.get<Dashboard[]>(`/db/objrubric/${idRubric}/exdashboard?type=` + (isMobile ? 'MOBILE' : 'WEB'));
  }

  create(title: string, type: string, idRubric, hidden = false, config = '', icon: IconModel): Observable<Dashboard> {
    return this.http.post<Dashboard>(`/wg/dashboard`, {
      dashname: title,
      type: type,
      objrubric: {id: idRubric},
      hidden,
      config,
      icon
    }).pipe(
      tap(() => {
        this.clearCache();
      })
    );
  }


  getByID(id): Observable<Dashboard> {
    return this.http.get<ServiceDashboardClass>(`/wg/dashboard/${id}`)
      .pipe(map((dashboard: ServiceDashboardClass) => (
        {...dashboard, config: JSON.parse(dashboard.config)}
      )));
  }

  delete(id) {
    return this.http.delete(`/wg/dashboard?id=${id}`);
  }

  update(id, data: Partial<ServiceDashboardClass | Dashboard>) {

    const params = data;
    if (typeof data.config !== 'string') {
      params.config = JSON.stringify(data.config);
    }
    return this.http.put(`/wg/dashboard/${id}`, params).pipe(
      tap(() => {
        this.clearCache();
      })
    );
  }

  getDasboardsCompatibleForCopy(id): Observable<Dashboard[]> {
    return this.http.get<Dashboard[]>(`/wg/dashboard/compatible/${id}`);
  }

  copy(id, controllerIds): Observable<Dashboard[]> {
    return this.http.post<Dashboard[]>(`/wg/dashboard/copy/${id}?${controllerIds.map(val => `controllerIds=${val}`).join('&')}`, {})
      .pipe(
        tap(() => {
          this.clearCache();
        })
      );
  }
  clone(id, rubricsIds): Observable<Dashboard[]> {
    // http://localhost:8080/sem-restservices/wg/dashboard/clone?rubricId=2&dashId=1
    return this.http.post<Dashboard[]>(`/wg/dashboard/clone?${rubricsIds.map(val => `rubricId=${val}`).join('&')}&dashId=${id}`, {})
      .pipe(
        tap(() => {
          this.clearCache();
        })
      );
  }

  searchParam(config: Partial<SearchParams>, objectId: number[]): Observable<IParamSearchModel[]> {
    let httpParams = new HttpParams();

    Object.keys(config).forEach((key: string) => {
      const value = config[key];
      if (value === undefined || value === null || value === '') {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item === undefined || item === null || item === '') {
            return;
          }
          httpParams = httpParams.append(key, String(item));
        });
      } else {
        httpParams = httpParams.append(key, String(value));
      }
    });

    objectId.forEach(val => {
      httpParams = httpParams.append('oid', String(val));
    });

    httpParams = httpParams.append('limit', '1000');
    return this.http.get<IParamSearchModel[]>(environment.backEndUrlPrefix + '/' + 'search/params', {params: httpParams});
  }

  searchParamsV2(
    config: Partial<operations['paramSearchFindV2']['parameters']['query']>,
    objectId?: string | number
  ): Observable<Array<components['schemas']['SearchParamsV2View']>> {
    let httpParams = new HttpParams();

    Object.keys(config).forEach((key: string) => {
      if (config[key]) {
        if (Array.isArray(config[key])) {
          (config[key] as any[]).forEach(val => {
            if (val !== undefined && val !== null && val !== '') {
              httpParams = httpParams.append(key, String(val));
            }
          });
        } else {
          httpParams = httpParams.append(key, String(config[key]));
        }
      }
    });
    if (objectId) {
      httpParams = httpParams.append('oid', String(objectId));
    }


    httpParams = httpParams.append('limit', '1000');

    return this.http.get<Array<components['schemas']['SearchParamsV2View']>>(
      `${environment.backEndUrlPrefix}/search/params/v2`,
      {params: httpParams}
    );
  }

  /** Сбрасываем кэш, чтобы getAll() на следующем вызове шёл на сервер и возвращал обновлённые данные */
  private clearCache(): void {
    this.dashboards = null;
    this.lastFetchTime = null;
    this.dashboards$ = null;
  }
}
